import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const AUTOSAVE_DEBOUNCE_MS = 500;

/**
 * Autosaves form data to `url` (private or public). Returns the loaded
 * record + helpers to mutate it. Pending edits are also flushed via
 * `keepalive` fetch on tab close so the consultant never loses data.
 */
export function useAutosaveInterview(url) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);
  const pendingPatchRef = useRef({});

  // Initial load
  useEffect(() => {
    api.get(url).then((r) => setData(r.data)).catch(() => toast.error("Not found"));
  }, [url]);

  const flushSave = async () => {
    if (!Object.keys(pendingPatchRef.current).length) return;
    const patch = pendingPatchRef.current;
    pendingPatchRef.current = {};
    setSaving(true);
    try {
      await api.put(url, patch);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const update = (patch) => {
    setData((prev) => ({ ...prev, ...patch }));
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, AUTOSAVE_DEBOUNCE_MS);
  };

  // Belt-and-suspenders: flush pending edits on tab close / navigate away.
  useEffect(() => {
    const beaconFlush = () => {
      if (!Object.keys(pendingPatchRef.current).length) return;
      const body = JSON.stringify(pendingPatchRef.current);
      pendingPatchRef.current = {};
      const headers = { "Content-Type": "application/json" };
      // Public links (`/public/*`) don't need auth. Private interviews need the Bearer header.
      if (!url.startsWith("/public/")) {
        const jwt = localStorage.getItem("gl_token");
        if (jwt) headers.Authorization = `Bearer ${jwt}`;
      }
      try {
        fetch(`${BACKEND_URL}/api${url}`, {
          method: "PUT",
          headers,
          body,
          keepalive: true,
        });
      } catch { /* best effort */ }
    };
    window.addEventListener("pagehide", beaconFlush);
    window.addEventListener("beforeunload", beaconFlush);
    return () => {
      window.removeEventListener("pagehide", beaconFlush);
      window.removeEventListener("beforeunload", beaconFlush);
      beaconFlush();
    };
  }, [url, BACKEND_URL]);

  return { data, setData, update, flushSave, saving };
}
